import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { CATEGORIES, PRIORITIES, ISSUE_TYPES } from '../../utils/helpers';
import AmcBanner from '../../components/shared/AmcBanner';
import ScreenshotUploader from '../../components/ScreenshotUploader';
import { useFeatures } from '../../utils/useFeatures';

export default function NewTicket() {
  const nav = useNavigate();

  // Step 1: product selection
  const [products, setProducts]     = useState([]);
  const [prodLoading, setProdLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null); // { product_name, serial_number }

  // Step 2: ticket form
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', category: 'general',
    issue_type: '', location: '', contact_name: '', contact_mobile: '', started_on: '', is_recurring: 0 });
  const [locations, setLocations] = useState([]);
  const [locOther, setLocOther] = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [shots, setShots] = useState([]);   // screenshot items to upload after create
  const { feature } = useFeatures();
  const attachEnabled = feature('attachments.enabled');
  const mobileBad = form.contact_mobile && !/^\d{10}$/.test(form.contact_mobile.replace(/\D/g, ''));

  useEffect(() => {
    apiFetch('/tickets/my-products')
      .then(data => { setProducts(data); })
      .catch(() => {})
      .finally(() => setProdLoading(false));
    apiFetch('/tickets/my-locations').then(d => setLocations(d || [])).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (mobileBad) { setError('Mobile number must be exactly 10 digits'); return; }
    setError(''); setLoading(true);
    try {
      const payload = {
        ...form,
        location: form.location === '__other' ? locOther.trim() : form.location,
        product_name: selectedProduct?.product_name || '',
        serial_number: selectedProduct?.serial_number || '',
      };
      const ticket = await apiFetch('/tickets', { method: 'POST', body: payload });
      // Upload screenshots (if any) to the freshly created ticket
      if (shots.length) {
        try {
          const token = sessionStorage.getItem('kgreen_token');
          const fd = new FormData();
          shots.forEach(it => fd.append('files', it.file, it.file.name));
          await fetch(`/api/tickets/${ticket.id}/attachments`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
        } catch (_) { /* ticket already created; ignore attachment failure */ }
      }
      nav(`/tickets/${ticket.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 1 — product picker ───────────────────────────────
  if (!selectedProduct) {
    return (
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 24px' }}>
        <AmcBanner />
        {/* Progress indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 28 }}>
          <StepDot n={1} active />
          <div style={{ flex: 1, height: 2, background: 'var(--border-subtle)' }} />
          <StepDot n={2} />
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Select Product</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 28 }}>
          Choose the product you need support for to raise a complaint.
        </p>

        {prodLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><span className="spinner" /></div>
        ) : products.length === 0 ? (
          <div className="card" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
            <p style={{ fontWeight: 600, marginBottom: 6 }}>No products linked to your account</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>
              Please contact your administrator to link your account to a purchased product.
            </p>
            <button className="btn btn-secondary" onClick={() => nav('/tickets')}>← Back to My Tickets</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {products.map(p => (
              <div key={p.id}
                onClick={() => setSelectedProduct(p)}
                style={{ cursor: 'pointer', border: '2px solid var(--border-subtle)', borderRadius: 12, padding: 20, background: 'var(--bg-surface)', transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                onMouseEnter={e => { e.currentTarget.style.border = '2px solid var(--status-resolved-text)'; e.currentTarget.style.background = 'var(--action-bg)'; }}
                onMouseLeave={e => { e.currentTarget.style.border = '2px solid var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 10, background: 'var(--action-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                    📦
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{p.product_name}</p>
                    <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
                      {p.serial_number && (
                        <span>S/N: <span style={{ fontFamily: 'monospace', fontWeight: 500, color: 'var(--text-muted)' }}>{p.serial_number}</span></span>
                      )}
                      {p.amc_end_date && (
                        <AmcBadge date={p.amc_end_date} />
                      )}
                    </div>
                  </div>
                </div>
                <span style={{ color: 'var(--status-resolved-text)', fontSize: 20 }}>›</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Step 2 — ticket form ──────────────────────────────────
  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 24px' }}>
      <AmcBanner />
      {/* Progress indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 28 }}>
        <StepDot n={1} done />
        <div style={{ flex: 1, height: 2, background: '#0B6623' }} />
        <StepDot n={2} active />
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Raise a Complaint</h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>
        Describe your issue and our team will respond as soon as possible.
      </p>

      {/* Selected product chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 10, padding: '12px 16px', marginBottom: 24 }}>
        <span style={{ fontSize: 20 }}>📦</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--status-resolved-text)' }}>{selectedProduct.product_name}</span>
          {selectedProduct.serial_number && (
            <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>S/N: {selectedProduct.serial_number}</span>
          )}
        </div>
        <button type="button" onClick={() => setSelectedProduct(null)}
          style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', padding: '2px 6px', borderRadius: 6 }}>
          ← Change
        </button>
      </div>

      {error && <div style={{ background: 'var(--red-50)', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>{error}</div>}

      <div className="card" style={{ padding: 28 }}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="label">Subject *</label>
            <input className="input" placeholder="Brief summary of your issue" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required autoFocus />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="label">Issue type</label>
              <select className="input" value={form.issue_type} onChange={e => setForm(f => ({ ...f, issue_type: e.target.value }))}>
                <option value="">Select…</option>
                {ISSUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="label">Category</label>
              <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('-', ' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="label">Priority</label>
              <select className="input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Location & contact */}
          <div className="form-group">
            <label className="label">Location / Branch</label>
            <select className="input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}>
              <option value="">Select a branch / site…</option>
              {locations.map(l => <option key={l.location} value={l.location}>{l.location}</option>)}
              <option value="__other">➕ Other (type a new location)</option>
            </select>
            {form.location === '__other' && (
              <input className="input" style={{ marginTop: 8 }} placeholder="New branch / site name"
                value={locOther} onChange={e => setLocOther(e.target.value)} />
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="label">Contact person</label>
              <input className="input" placeholder="Who should we reach?" value={form.contact_name}
                onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="label">Contact mobile</label>
              <input className="input" placeholder="10-digit mobile" value={form.contact_mobile}
                onChange={e => setForm(f => ({ ...f, contact_mobile: e.target.value }))}
                style={mobileBad ? { borderColor: '#dc2626' } : undefined} />
              {mobileBad && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>Mobile number must be exactly 10 digits</div>}
            </div>
          </div>

          {/* Timing */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="label">When did it start? <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
              <input className="input" type="date" value={form.started_on}
                onChange={e => setForm(f => ({ ...f, started_on: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="label">Is it recurring?</label>
              <div style={{ display: 'inline-flex', background: 'var(--n-100)', borderRadius: 9, padding: 3, gap: 2 }}>
                {[[0, 'No — first time'], [1, 'Yes — repeatedly']].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => setForm(f => ({ ...f, is_recurring: v }))}
                    style={{ border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '7px 14px', borderRadius: 7,
                      background: form.is_recurring === v ? 'var(--bg-surface)' : 'transparent',
                      color: form.is_recurring === v ? '#0B6623' : 'var(--text-secondary)',
                      boxShadow: form.is_recurring === v ? '0 1px 2px rgba(0,0,0,.1)' : 'none' }}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="label">Description *</label>
            <textarea className="input" rows={6} placeholder="Please provide as much detail as possible…"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              required style={{ resize: 'vertical' }} />
          </div>

          {attachEnabled && (
          <div className="form-group">
            <label className="label">Screenshots <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
            <ScreenshotUploader items={shots} setItems={setShots} disabled={loading} />
          </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => nav(-1)}>Cancel</button>
            <button className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : 'Submit Complaint'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Step dot indicator
function StepDot({ n, active, done }) {
  const bg = done ? '#0B6623' : active ? 'var(--status-resolved-text)' : 'var(--border-subtle)';
  const color = (active || done) ? '#fff' : 'var(--text-secondary)';
  return (
    <div style={{ width: 30, height: 30, borderRadius: '50%', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
      {done ? '✓' : n}
    </div>
  );
}

// AMC expiry badge
function AmcBadge({ date }) {
  const days = Math.ceil((new Date(date) - new Date()) / 86400000);
  if (days < 0) return <span style={{ color: '#b91c1c', fontWeight: 600 }}>⚠ AMC Expired</span>;
  if (days <= 30) return <span style={{ color: '#92400e' }}>AMC expires in {days}d</span>;
  return <span style={{ color: 'var(--status-resolved-text)' }}>AMC Active</span>;
}
